import type { SetupScreen, SetupStepNavigator } from './setup-screen'

/** The competitors screens, in order. The index of a screen is its place in setup. */
export const COMPETITORS_SCREENS = [
  { id: 'known', transient: false },
  { id: 'name', transient: false },
  { id: 'finding', transient: true },
  { id: 'review', transient: false },
] as const satisfies readonly SetupScreen[]

/** One competitors screen. A failed discovery is the failed variant of `finding`. */
export type CompetitorsScreen = (typeof COMPETITORS_SCREENS)[number]['id']

export type CompetitorsMove =
  /** "I know a few" on the choice. */
  | 'name-known'
  /** A discovery started from the choice or from the named companies. */
  | 'discovery-started'
  /** The discovery on show has finished and the ring has burst. */
  | 'discovery-landed'
  /** Back on the list. */
  | 'back-to-known'

export type CompetitorsDiscoveryState = 'none' | 'live' | 'failed' | 'settled'

/** What the saved data holds, reduced to what the step turns on. */
export interface CompetitorsState {
  /** How many competitors the brand tracks. */
  readonly trackedCount: number
  /** Where the latest discovery stands. */
  readonly discovery: CompetitorsDiscoveryState
  /** True when the competitors were confirmed after the brand facts last were. */
  readonly confirmedCurrent: boolean
  /** True when the latest discovery started after the brand facts were last confirmed. */
  readonly discoveryCurrent: boolean
}

/** Where the step stands from the saved data: `choose`, `finding`, `review` or `done`. */
export type CompetitorsPhase = 'choose' | 'finding' | 'review' | 'done'

export function competitorsPhase(state: CompetitorsState): CompetitorsPhase {
  if (state.trackedCount > 0 && state.confirmedCurrent) return 'done'
  switch (state.discoveryCurrent ? state.discovery : 'none') {
    case 'none':
      return 'choose'
    case 'live':
    case 'failed':
      return 'finding'
    case 'settled':
      return 'review'
  }
}

/** The screen a phase opens on. A step that is done opens on the list the founder confirmed. */
export function competitorsPhaseScreen(phase: CompetitorsPhase): CompetitorsScreen {
  switch (phase) {
    case 'choose':
      return 'known'
    case 'finding':
      return 'finding'
    case 'review':
    case 'done':
      return 'review'
  }
}

export function resumeCompetitorsScreen(state: CompetitorsState): CompetitorsScreen {
  return competitorsPhaseScreen(competitorsPhase(state))
}

/** The screen a move leads to. A move that does not apply to the screen on show keeps it. */
export function moveCompetitors(from: CompetitorsScreen, move: CompetitorsMove): CompetitorsScreen {
  switch (move) {
    case 'name-known':
      return from === 'known' ? 'name' : from
    case 'discovery-started':
      return from === 'known' || from === 'name' ? 'finding' : from
    case 'discovery-landed':
      return from === 'finding' ? 'review' : from
    case 'back-to-known':
      return from === 'review' ? 'known' : from
  }
}

export const COMPETITORS_NAVIGATOR: SetupStepNavigator<CompetitorsScreen, CompetitorsMove> = {
  screens: COMPETITORS_SCREENS,
  move: moveCompetitors,
}
